import React, {
  useEffect,
  useRef,
  useCallback,
  forwardRef,
  useImperativeHandle,
  useState,
} from "react";
import { Box, Flex, Text, Button, Card } from "@radix-ui/themes";
import { Loader2, AlertTriangle } from "lucide-react";
import { IAny } from "@/types/breeze/Any";
import { loadGoogleMapsApi, cleanupGoogleMapsApi } from "./MapLoader";
import { useAuth } from "@/providers/AuthProvider";
import { useTenant } from "@/hooks/useCommonQueries";


// Marker interface
export interface MapMarker {
  id: string;
  position: {
    lat: number;
    lng: number;
  };
  isActive?: boolean;
  data: IAny;
  draggable?: boolean;
}

// Define the type for the ref methods
export interface MapRef {
  centerOnMarker: (markerId: string) => void;
}

export interface MapProps {
  markers?: MapMarker[];
  center?: { lat: number; lng: number };
  zoom?: number;
  onMapClick?: (event: google.maps.MapMouseEvent) => void;
  onMarkerClick?: (markerId: string) => void;
  onMarkerCentered?: (markerId: string) => void;
  onMarkerDragEnd?: (
    markerId: string,
    newPosition: { lat: number; lng: number },
  ) => void;
  className?: string;
  readOnly?: boolean;
}


// The main Map component
const Map = forwardRef<MapRef, MapProps>(
  (
    {
      markers = [],
      center = { lat: 51.505, lng: -0.09 }, // London as default
      zoom = 13,
      onMapClick,
      onMarkerClick,
      onMarkerCentered,
      onMarkerDragEnd,
      className = "",
      readOnly = false,
    },
    ref,
  ) => {
    const mapRef = useRef<HTMLDivElement>(null);
    const mapInstanceRef = useRef<google.maps.Map | null>(null);
    const markersRef = useRef<Record<string, google.maps.Marker>>({});
    const infoWindowsRef = useRef<Record<string, google.maps.InfoWindow>>({});
    const isInitialFitRef = useRef(true);
    const [mapError, setMapError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    const { currentTenant } = useAuth();
    const { data: tenant, isLoading: isTenantLoading } = useTenant(
      currentTenant?.id || 0,
    );

    // Initialize map and markers function
    const initializeMap = useCallback(() => {
      try {
        if (!mapRef.current || !window.google?.maps) {
          setMapError("Google Maps API not loaded");
          return;
        }

        const { maps } = window.google;

        const mapOptions: google.maps.MapOptions = {
          center,
          zoom,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: !readOnly,
          gestureHandling: readOnly ? "none" : "cooperative",
          zoomControl: !readOnly,
          keyboardShortcuts: !readOnly,
          disableDoubleClickZoom: readOnly,
        };

        // Create map if needed
        if (!mapInstanceRef.current) {
          try {
            mapInstanceRef.current = new maps.Map(mapRef.current, mapOptions);
          } catch (err) {
            console.error("Error creating map instance:", err);
            setMapError("Could not create map. Please try again.");
            return;
          }
        } else {
          mapInstanceRef.current.setOptions(mapOptions);
        }

        const map = mapInstanceRef.current;
        if (!map) return;

        // Clean up previous markers
        Object.values(markersRef.current).forEach((marker) => {
          marker.setMap(null);
        });

        Object.values(infoWindowsRef.current).forEach((infoWindow) => {
          infoWindow.close();
        });

        // Reset references
        markersRef.current = {};
        infoWindowsRef.current = {};

        // Add click listener to map (only once)
        maps.event.clearListeners(map, "click");
        if (onMapClick && !readOnly) {
          maps.event.addListener(
            map,
            "click",
            (event: google.maps.MapMouseEvent) => {
              onMapClick(event);
            },
          );
        }

        // Handle no markers case
        if (markers.length === 0) {
          map.setCenter(center);
          map.setZoom(zoom);
          setMapError(null);
          setIsLoading(false);
          return;
        }

        // Add markers and calculate bounds
        let boundsExist = false;
        const bounds = new maps.LatLngBounds();

        markers.forEach((markerData) => {
          // Update bounds
          bounds.extend(markerData.position);
          boundsExist = true;

          // Create marker icon with pin shape
          const icon: google.maps.Symbol = {
            // SVG path for a pin - standard Google Maps pin shape
            path: "M12,2C8.14,2 5,5.14 5,9c0,5.25 7,13 7,13s7,-7.75 7,-13c0,-3.86 -3.14,-7 -7,-7zM12,13c-2.21,0 -4,-1.79 -4,-4s1.79,-4 4,-4 4,1.79 4,4 -1.79,4 -4,4z",
            fillColor: markerData.isActive ? "#3B82F6" : "#6B7280",
            fillOpacity: markerData.isActive ? 1 : 0.8,
            strokeWeight: markerData.isActive ? 2 : 1,
            strokeColor: "#FFFFFF",
            strokeOpacity: 1,
            scale: markerData.isActive ? 2 : 1.8,
            anchor: new maps.Point(12, 22),
            labelOrigin: new maps.Point(12, 9),
          };

          // Create marker
          const marker = new maps.Marker({
            position: markerData.position,
            map,
            title: String(markerData.data.title || ""),
            draggable: readOnly ? false : (markerData.draggable || false),
            icon,
          });

          // Store marker reference
          markersRef.current[markerData.id] = marker;

          // Create info window with simpler content
          const infoWindow = new maps.InfoWindow({
            content: `
            <div style="padding: 5px; max-width: 200px;">
              <style>
                .gm-ui-hover-effect { display: none !important; }
                .gm-style .gm-style-iw-c { padding-right: 12px !important; }
              </style>
              <div style="font-weight: 500;">${
                markerData.data.title || ""
              }</div>
              ${
                markerData.data.description
                  ? `<div style="font-size: 12px; margin-top: 3px;">${markerData.data.description}</div>`
                  : ""
              }
            </div>
          `,
            disableAutoPan: true,
            pixelOffset: new maps.Size(0, -5),
          });

          // Store info window reference
          infoWindowsRef.current[markerData.id] = infoWindow;

          // Add click listener
          maps.event.addListener(marker, "click", () => {
            if (onMarkerClick) {
              onMarkerClick(markerData.id);

              // Briefly bounce the marker
              marker.setAnimation(maps.Animation.BOUNCE);
              setTimeout(() => {
                marker.setAnimation(null);
              }, 750);

              // Pan to marker if not already active
              if (!markerData.isActive) {
                map.panTo(markerData.position);
                if (map.getZoom()! < 14) map.setZoom(14);
              }
            }
          });

          // Add drag end listener
          if (markerData.draggable && onMarkerDragEnd) {
            maps.event.addListener(marker, "dragend", () => {
              const newPos = marker.getPosition();
              if (newPos) {
                const newPosition = {
                  lat: newPos.lat(),
                  lng: newPos.lng(),
                };
                onMarkerDragEnd(markerData.id, newPosition);
              }
            });
          }

          // Add hover listeners
          maps.event.addListener(marker, "mouseover", () => {
            infoWindow.open(map, marker);
          });

          maps.event.addListener(marker, "mouseout", () => {
            infoWindow.close();
          });

          // Briefly bounce marker if it's active on initial render
          if (markerData.isActive) {
            marker.setAnimation(maps.Animation.BOUNCE);
            setTimeout(() => {
              marker.setAnimation(null);
            }, 750);

            // Center on active marker
            map.panTo(markerData.position);
            if (map.getZoom()! < 14) map.setZoom(14);
          }
        });

        // Only fit bounds if we have markers and it's the initial render
        if (boundsExist && isInitialFitRef.current) {
          try {
            // Fit bounds with padding
            map.fitBounds(bounds);

            // Limit max zoom
            maps.event.addListenerOnce(map, "idle", () => {
              const currentZoom = map.getZoom();
              if (currentZoom && currentZoom > 16) map.setZoom(16);
            });

            // Don't fit bounds again unless markers change
            isInitialFitRef.current = false;
          } catch (err) {
            console.error("Error fitting bounds:", err);
            // Fallback to center/zoom
            map.setCenter(center);
            map.setZoom(zoom);
          }
        }

        setMapError(null);
        setIsLoading(false);
      } catch (err) {
        console.error("Error initializing map:", err);
        setMapError("Error loading map. Please try again.");
        setIsLoading(false);
      }
    }, [markers, center, zoom, onMapClick, onMarkerClick, onMarkerDragEnd, readOnly]);

    // Load Google Maps API and initialize map
    useEffect(() => {
      // Don't try to load or set error if tenant data is still loading
      if (isTenantLoading || !tenant) {
        return;
      }

      // Don't try to load if API key is missing
      if (!tenant.google_maps_key || tenant.google_maps_key.trim() === "") {
        setMapError("Google Maps API key is not configured");
        setIsLoading(false);
        return;
      }

      setMapError(null);
      setIsLoading(true);

      const handleMapLoaded = (error?: Error) => {
        if (error) {
          setMapError(error.message);
          setIsLoading(false);
        } else {
          initializeMap();
        }
      };

      try {
        loadGoogleMapsApi(tenant.google_maps_key, handleMapLoaded);
      } catch (error) {
        console.error("Error loading Google Maps:", error);
        setMapError("Failed to load Google Maps. Please try again.");
        setIsLoading(false);
      }

      return () => {
        cleanupGoogleMapsApi(handleMapLoaded);
      };
    }, [initializeMap, tenant?.google_maps_key, isTenantLoading, !tenant]);

    // Re-initialize map when markers change
    useEffect(() => {
      if (window.google?.maps && mapRef.current) {
        initializeMap();
      }
    }, [markers, initializeMap]);

    // Function to center on a specific marker
    const centerOnMarker = useCallback(
      (markerId: string) => {
        try {
          const marker = markersRef.current[markerId];
          const map = mapInstanceRef.current;

          if (!marker || !map) {
            console.warn("Could not center on marker:", markerId);
            return false;
          }

          // Get the marker's position
          const markerData = markers.find((m) => m.id === markerId);
          if (!markerData) {
            console.warn("Could not find marker data:", markerId);
            return false;
          }

          // Pan to the marker position
          map.panTo(markerData.position);

          // Set minimum zoom level
          const currentZoom = map.getZoom();
          if (currentZoom && currentZoom < 14) {
            map.setZoom(14);
          }

          // Bounce the marker after a short delay
          setTimeout(() => {
            if (window.google?.maps) {
              marker.setAnimation(window.google.maps.Animation.BOUNCE);

              // Stop the animation after a period for visibility
              setTimeout(() => {
                marker.setAnimation(null);
              }, 1500);

              // Call the callback if provided
              if (onMarkerCentered) {
                onMarkerCentered(markerId);
              }
            }
          }, 200);

          return true;
        } catch (error) {
          console.error("Error in centerOnMarker:", error);
          return false;
        }
      },
      [markers, onMarkerCentered],
    );

    // Expose the centerOnMarker function via ref
    useImperativeHandle(
      ref,
      () => ({
        centerOnMarker,
      }),
      [centerOnMarker],
    );

    // Function to retry loading the map
    const retryMapLoad = useCallback(() => {
      // Don't try to load if API key is missing
      if (!tenant?.google_maps_key || tenant.google_maps_key.trim() === "") {
        setMapError("Google Maps API key is not configured");
        setIsLoading(false);
        return;
      }

      setMapError(null);
      setIsLoading(true);

      // Reset map instance
      mapInstanceRef.current = null;

      // Clear existing markers
      markersRef.current = {};
      infoWindowsRef.current = {};

      // Reset the initial fit flag
      isInitialFitRef.current = true;

      // Try loading the API again
      setTimeout(() => {
        if (tenant?.google_maps_key) {
          loadGoogleMapsApi(tenant.google_maps_key, () => {
            initializeMap();
          });
        }
      }, 100);
    }, [initializeMap, tenant?.google_maps_key]);

    return (
      <Box style={{ position: "relative", width: "100%", height: "100%" }}>
        <Box
          ref={mapRef}
          style={{ height: "100%", width: "100%" }}
          className={className}
        />

        {isLoading && !mapError && (
          <Box
            style={{
              position: "absolute",
              inset: 0,
              zIndex: 1,
            }}
          >
            <Flex
              align="center"
              justify="center"
              style={{
                width: "100%",
                height: "100%",
                backgroundColor: "var(--gray-2)",
                opacity: 0.7,
              }}
            >
              <Flex direction="column" align="center" gap="3">
                <Loader2
                  style={{
                    width: "2.5rem",
                    height: "2.5rem",
                    animation: "spin 1s linear infinite",
                    color: "var(--accent-9)",
                  }}
                />
                <Text size="2" color="gray">
                  Loading map...
                </Text>
              </Flex>
            </Flex>
          </Box>
        )}

        {mapError && (
          <Box
            style={{
              position: "absolute",
              inset: 0,
              zIndex: 10,
            }}
          >
            <Flex
              align="center"
              justify="center"
              style={{
                width: "100%",
                height: "100%",
                backgroundColor: "var(--gray-2)",
                opacity: 0.9,
              }}
            >
              <Card style={{ maxWidth: "28rem", width: "100%" }}>
                <Box p="4">
                  <Flex direction="column" align="center" gap="3">
                    <AlertTriangle
                      size={48}
                      style={{ color: "var(--amber-9)" }}
                    />
                    <Text size="5" weight="bold">
                      Map Error
                    </Text>
                    <Text size="3" color="gray" align="center">
                      {mapError}
                    </Text>
                    <Button onClick={retryMapLoad} variant="solid" color="blue">
                      Retry Loading Map
                    </Button>
                  </Flex>
                </Box>
              </Card>
            </Flex>
          </Box>
        )}
      </Box>
    );
  },
);

// Set display name for the component
Map.displayName = "Map";

export default Map;
